import { useCallback } from 'react';
import type { Selection } from 'react-aria-components';
import { useLocation } from 'react-router-dom';
import { Avatar, Button, Description, Label, ListBox, Popover, Separator } from '@heroui/react';
import { IconPlus, IconSwitch3 } from '@tabler/icons-react';
import { Sidebar, useSidebar } from '@heroui-pro/react';
import type { User } from '@hs/shared/stores/slices/authSlice';
import { updateUserInfo } from '@hs/shared/stores/slices/authSlice';
import { AppIcon } from '@/components/AppIcon';
import { trpc } from '@/lib/trpc';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { useSortedApps, type SidebarAppItem } from './use-sorted-apps';

function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}年${month}月${day}日`;
  } catch {
    return dateString;
  }
}

function getSpaceInitial(name: string | null | undefined): string {
  if (!name || name.trim() === '') {
    return 'S';
  }
  return name.charAt(0);
}

type SpaceRow = NonNullable<User['spaces']>[number];

type AvatarSize = 'sm' | 'md' | 'lg';

/** 空间类型展示（接口值为枚举英文） */
const SPACE_TYPE_LABEL: Record<string, string> = {
  PERSONAL: '个人',
  TEAM: '团队',
  ENTERPRISE: '企业',
  BRANCH: '企业',
};

function spaceKindSubtitle(space: SpaceRow): string {
  const raw = space.type?.trim();
  if (raw) return SPACE_TYPE_LABEL[raw] ?? raw;
  return '—';
}

/** 弹层副标题：与 UI 稿一致，如「个人空间」「团队空间」 */
function spaceKindDescription(space: SpaceRow): string {
  const sub = spaceKindSubtitle(space);
  if (!sub || sub === '—') return '空间';
  return `${sub}空间`;
}

function SpaceAvatar({
  space,
  size = 'md',
  className,
}: {
  space: SpaceRow;
  size?: AvatarSize;
  className?: string;
}) {
  return (
    <Avatar className={className} size={size} variant="soft">
      {space.logoUrl ? (
        <Avatar.Image alt={space.name} src={space.logoUrl} />
      ) : null}
      <Avatar.Fallback className="font-semibold">{getSpaceInitial(space.name)}</Avatar.Fallback>
    </Avatar>
  );
}

function routePathForApp(app: SidebarAppItem) {
  return `/${app.code}`;
}

function isAppNavActive(pathname: string, app: SidebarAppItem) {
  const routePath = routePathForApp(app);
  return pathname === routePath || (routePath !== '/' && pathname.startsWith(`${routePath}/`));
}

/** 侧栏内应用列表（Sidebar.Content），与 template-finances 中 Sidebar.Menu 导航同级。 */
function SidebarAppsNav() {
  const location = useLocation();
  const apps = useSortedApps();

  if (apps.length === 0) {
    return null;
  }

  return (
    <Sidebar.Group>
      <Sidebar.Menu aria-label="应用导航">
        {apps.map((app) => (
          <Sidebar.MenuItem
            key={app.id}
            href={routePathForApp(app)}
            id={app.id}
            isCurrent={isAppNavActive(location.pathname, app)}
            textValue={app.name}
            tooltip={app.name}
          >
            <Sidebar.MenuIcon>
              <AppIcon icon={app.icon} className="size-4" />
            </Sidebar.MenuIcon>
            <Sidebar.MenuLabel>{app.name}</Sidebar.MenuLabel>
          </Sidebar.MenuItem>
        ))}
      </Sidebar.Menu>
    </Sidebar.Group>
  );
}

/**
 * 侧栏当前空间展示（无交互弹层）。
 * `data-sidebar="label"` 挂在文案列上，icon 折叠态与菜单同样隐藏且不占位。
 */
function SpaceInfo({ space }: { space: SpaceRow | null }) {
  if (!space) {
    return (
      <div className="sidebar-header-row flex min-w-0 flex-1 items-center gap-2 opacity-60">
        <Avatar size="sm" variant="soft">
          <Avatar.Fallback className="text-xs">S</Avatar.Fallback>
        </Avatar>
        <div className="flex min-w-0 flex-col" data-sidebar="label">
          <p className="truncate text-sm font-medium">未加入空间</p>
          <p className="text-muted truncate text-xs">—</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sidebar-header-row flex min-w-0 flex-1 items-center gap-2">
      <SpaceAvatar size="sm" space={space} />
      <div className="flex min-w-0 flex-col" data-sidebar="label">
        <p className="truncate text-sm font-medium">{space.name}</p>
        <p className="text-muted truncate text-xs">{spaceKindDescription(space)}</p>
      </div>
    </div>
  );
}

/** 空间切换、创建入口：由右侧 IconSwitch3 按钮打开 Popover。 */
function SpaceSwitcherPopover() {
  const dispatch = useAppDispatch();
  const { collapsible, isMobile, isOpen } = useSidebar();
  const user = useAppSelector((state) => state.auth.user);
  const spaces = user?.spaces ?? [];
  const currentSpace = spaces.length > 0 ? spaces[0] : null;
  const otherSpaces = spaces.length > 1 ? spaces.slice(1) : [];

  const switchSpaceMutation = trpc.auth.switchSpace.useMutation();

  const subscriptionEndRaw = currentSpace?.subscription?.endTime?.trim();
  const subscriptionEndTime = subscriptionEndRaw ? formatDate(subscriptionEndRaw) : null;

  const switchToSpace = useCallback(
    async (targetSpaceId: string) => {
      if (!user?.spaces?.length) return;
      if (user.spaces[0]?.id === targetSpaceId) return;

      try {
        const nextUser = await switchSpaceMutation.mutateAsync({ spaceId: targetSpaceId });
        dispatch(updateUserInfo({ user: nextUser as User }));
      } catch {
        // 切换失败时保持当前空间，避免前端状态与后端不一致
      }
    },
    [dispatch, switchSpaceMutation, user]
  );

  const handleOtherSpacesSelectionChange = useCallback(
    (keys: Selection) => {
      if (keys === 'all' || keys.size === 0) return;
      const id = [...keys][0] as string;
      void switchToSpace(id);
    },
    [switchToSpace]
  );

  if (!currentSpace) {
    return null;
  }

  /** 与 Sidebar 内菜单一致：icon 折叠轨 + 桌面端收起时不显示切换入口 */
  if (collapsible === 'icon' && !isMobile && !isOpen) {
    return null;
  }

  return (
    <Popover>
      <Popover.Trigger
        className="sidebar-header-trigger shrink-0 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Button
          isIconOnly
          aria-label="切换或管理空间"
          className="shrink-0"
          size="sm"
          variant="ghost"
        >
          <IconSwitch3 className="size-4" stroke={1.5} />
        </Button>
      </Popover.Trigger>

      <Popover.Content className="w-[320px]" placement="bottom end" offset={6}>
        <Popover.Dialog>
          <div className="text-muted px-0 pb-2 text-xs font-medium">当前空间</div>
          <div className="flex min-w-0 items-center gap-3">
            <SpaceAvatar size="md" space={currentSpace} />
            <div className="flex min-w-0 flex-col gap-0.5">
              <Label className="truncate text-base font-semibold">{currentSpace.name}</Label>
              <Description className="truncate">{spaceKindDescription(currentSpace)}</Description>
            </div>
          </div>
          {subscriptionEndTime ? (
            <p className="text-muted mt-3 text-sm">订阅截止日：{subscriptionEndTime}</p>
          ) : currentSpace?.subscription ? (
            <p className="text-muted mt-3 text-sm">订阅有效期：永久</p>
          ) : null}

          <Separator className="my-3" />

          {otherSpaces.length > 0 ? (
            <div className="flex flex-col gap-2">
              <div className="text-muted px-0 text-xs font-medium">切换空间</div>
              <ListBox
                aria-label="切换空间"
                className="w-full max-w-full gap-0.5 p-0"
                selectionMode="single"
                onSelectionChange={handleOtherSpacesSelectionChange}
              >
                {otherSpaces.map((space: SpaceRow) => (
                  <ListBox.Item
                    key={space.id}
                    id={space.id}
                    textValue={space.name}
                    className="h-auto w-full gap-2 rounded-md px-2 py-2"
                  >
                    <SpaceAvatar size="sm" space={space} />
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <Label className="truncate">{space.name}</Label>
                      <Description className="truncate">{spaceKindDescription(space)}</Description>
                    </div>
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                ))}
              </ListBox>
            </div>
          ) : null}

          <Button className="mt-3 w-full justify-start gap-2" variant="ghost" onPress={() => {}}>
            <IconPlus className="size-4 shrink-0" stroke={1.5} />
            <span>创建新空间</span>
          </Button>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

/** 桌面与移动侧栏共用的 Header + Content（参考 finances-sidebar 的 SidebarContents）。 */
function SidebarBody() {
  const currentSpace = useAppSelector((s) => s.auth.user)?.spaces?.[0] ?? null;

  return (
    <>
      <Sidebar.Header className="border-divider shrink-0">
        <div className="sidebar-header-slot flex min-h-0 w-full min-w-0 shrink-0 items-center gap-1 py-2">
          <SpaceInfo space={currentSpace} />
          <SpaceSwitcherPopover />
        </div>
      </Sidebar.Header>
      <Sidebar.Content>
        <SidebarAppsNav />
      </Sidebar.Content>
    </>
  );
}

/**
 * 左侧边栏（AppLayout.sidebar）：空间信息 + 应用列表。
 */
export function AppSidebar() {
  return (
    <>
      <Sidebar>
        <SidebarBody />
        <Sidebar.Rail />
      </Sidebar>

      <Sidebar.Mobile backdrop="blur">
        <SidebarBody />
      </Sidebar.Mobile>
    </>
  );
}
